/**
 * VoiceRoomCard Component
 * Displays voice room with participants for Digital Office sidebar
 */

import React, { useState } from 'react';
import { Volume2, Circle, ChevronRight, Settings } from 'lucide-react';
import type { VoiceRoom, VoiceRoomSession } from '@/types/voice-session.types';
import { VoiceUserCard } from './VoiceUserCard';
import { VoiceRoomSettingsModal } from './VoiceRoomSettingsModal';
import { useLiveKit } from '@/contexts/LiveKitContext';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/auth.store';

interface VoiceRoomCardProps {
  voiceRoom: VoiceRoom;
  voiceRoomSession: VoiceRoomSession | null;
  isUserConnected: boolean;
}

export const VoiceRoomCard: React.FC<VoiceRoomCardProps> = ({
  voiceRoom,
  voiceRoomSession,
  isUserConnected,
}) => {
  const { joinVoiceRoom, leaveVoiceRoom } = useLiveKit();
  const { user } = useAuth();
  const { user: authUser } = useAuthStore();
  const [isHovered, setIsHovered] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const isAdmin = authUser?.role === 'admin' || authUser?.role === 'super_admin';

  const participantCount = voiceRoomSession?.participants.length || 0;
  const isEmpty = participantCount === 0;

  const handleClick = () => {
    if (isUserConnected) {
      leaveVoiceRoom();
    } else {
      joinVoiceRoom(voiceRoom.id);
    }
  };

  return (
    <div
      className={`group rounded-md transition-colors ${isUserConnected ? 'bg-green-500/10 border border-green-500/30' : ''}`}
      data-testid="voice-room-card"
    >
      {/* Room Header */}
      <button
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
          isEmpty ? 'text-gray-500 hover:bg-gray-800' :
          !isUserConnected ? 'text-gray-200 hover:bg-gray-800' :
          'text-green-400 hover:bg-green-500/20'
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        title={isUserConnected ? `Напусни: ${voiceRoom.name}` : `Влез в: ${voiceRoom.name}`}
      >
        {isEmpty ? (
          <Circle className="w-4 h-4 text-gray-600" />
        ) : (
          <Volume2 className={`w-4 h-4 ${isUserConnected ? 'text-green-400' : 'text-gray-400'}`} />
        )}

        <span className="flex-1 text-left text-sm font-medium truncate">{voiceRoom.name}</span>

        {/* Settings Button (visible on hover, admin only) */}
        {isAdmin && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowSettings(true);
            }}
            className={`flex-shrink-0 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${
              isUserConnected
                ? 'text-green-300/70 hover:text-green-200 hover:bg-green-500/10'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
            }`}
            title="Настройки на стаята"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        )}

        {!isEmpty && (
          <span
            className={`px-2 py-0.5 text-xs rounded font-medium ${
              isUserConnected
                ? 'bg-green-600/30 text-green-300'
                : 'bg-gray-700 text-gray-300'
            }`}
          >
            {participantCount}
          </span>
        )}

        {isHovered && !isUserConnected && (
          <ChevronRight className="w-4 h-4 text-gray-400 animate-in slide-in-from-left-1 duration-200" />
        )}
      </button>

      {/* Participants List */}
      {!isEmpty && voiceRoomSession && (
        <div className="mt-1 space-y-0.5 px-2 pb-2">
          {voiceRoomSession.participants.map((participant) => (
            <VoiceUserCard
              key={participant.userId}
              participant={participant}
              isCurrentUser={participant.userId === user?.id}
            />
          ))}
        </div>
      )}

      {/* Voice Room Settings Modal */}
      {showSettings && (
        <VoiceRoomSettingsModal
          voiceRoom={voiceRoom}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};
